import React, { useState } from 'react';
import {
  Mail,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Server,
  Key,
  Globe,
  FileText,
  UserX,
  Send,
  Eye,
  Lock,
  XCircle
} from 'lucide-react';
import { INITIAL_EMAIL_DELIVERABILITY, INITIAL_COMMUNICATION_TEMPLATES } from '../../data/growthData';
import { EmailDeliverabilityStats, CommunicationTemplateItem } from '../../types';

export const EmailDeliverabilityHub: React.FC = () => {
  const [stats] = useState<EmailDeliverabilityStats>(INITIAL_EMAIL_DELIVERABILITY);
  const [templates] = useState<CommunicationTemplateItem[]>(INITIAL_COMMUNICATION_TEMPLATES);
  const [selectedTemplate, setSelectedTemplate] = useState<CommunicationTemplateItem>(INITIAL_COMMUNICATION_TEMPLATES[0]);
  const [activeTab, setActiveTab] = useState<'deliverability' | 'templates' | 'suppression'>('deliverability');

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-900">
              P12.4 — Email & Deliverability Engine
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              DMARC p=reject Protection Active
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Email Deliverability, DNS Authentication & Communication Templates
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Isolates transactional security streams from promotional lifecycle communications. Enforces SPF, DKIM, DMARC alignment, auto-suppression on hard bounces, and variable allowlists.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('deliverability')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'deliverability' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Deliverability & DNS
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'templates' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Template Inspector
          </button>
          <button
            onClick={() => setActiveTab('suppression')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'suppression' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Central Suppression List
          </button>
        </div>
      </div>

      {activeTab === 'deliverability' && (
        <div className="space-y-6">
          {/* Key Deliverability Telemetry Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">24h Dispatched</span>
              <span className="text-lg font-black text-slate-900">{stats.sent24h.toLocaleString()}</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">Delivery Rate</span>
              <span className="text-lg font-black text-emerald-700">{stats.deliveryRatePct}%</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">Bounce Rate</span>
              <span className="text-lg font-black text-slate-800">{stats.bounceRatePct}%</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">Spam Complaint Rate</span>
              <span className="text-lg font-black text-emerald-700">{stats.complaintRatePct}%</span>
              <span className="text-[10px] text-emerald-600 block mt-0.5">Target &lt; 0.1%</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">Unsubscribe Rate</span>
              <span className="text-lg font-black text-slate-800">{stats.unsubscribeRatePct}%</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] block font-medium">Suppression List</span>
              <span className="text-lg font-black text-rose-700">{stats.suppressionListCount}</span>
            </div>
          </div>

          {/* Stream Isolation & DNS Security */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-blue-900" />
                <h3 className="text-sm font-bold text-slate-900">Email Stream Isolation Architecture</h3>
              </div>
              <p className="text-xs text-slate-600">
                YamooPay separates high-reputation transactional emails from promotional lifecycle campaigns to ensure account security messages are never blocked by inbox providers.
              </p>

              <div className="space-y-2 text-xs">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-emerald-950 block">Transactional Stream (Security & Magic Links)</span>
                    <span className="text-emerald-800 text-[11px]">Passkey confirmations, security alerts, export readiness</span>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-700 text-white">PROTECTED 100%</span>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between">
                  <div>
                    <span className="font-bold text-blue-950 block">Lifecycle Marketing Stream</span>
                    <span className="text-blue-800 text-[11px]">Saved scenario updates, rate alerts, tax year updates</span>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-700 text-white">RATE LIMITED</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-emerald-700" />
                <h3 className="text-sm font-bold text-slate-900">DNS Authentication & Anti-Spoofing Status</h3>
              </div>

              <div className="space-y-2 text-xs">
                <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                  <span className="font-medium text-slate-700">SPF Record (Sender Policy Framework)</span>
                  <span className="flex items-center gap-1 font-bold text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    PASS (v=spf1 include:_spf.yamoopay.com ~all)
                  </span>
                </div>

                <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                  <span className="font-medium text-slate-700">DKIM Signature (2048-bit Key)</span>
                  <span className="flex items-center gap-1 font-bold text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    PASS (s=202608 d=yamoopay.com)
                  </span>
                </div>

                <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                  <span className="font-medium text-slate-700">DMARC Policy Enforcement</span>
                  <span className="flex items-center gap-1 font-bold text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    PASS (v=DMARC1; p=reject; pct=100)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-5">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Communication Template System & Variable Allowlist</h3>
              <p className="text-xs text-slate-600 mt-0.5">
                Ensures all campaign messages use localized, approved templates with strictly allowlisted variables.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-700 block uppercase tracking-wider">Approved Templates</span>
              {templates.map(tmpl => (
                <div
                  key={tmpl.id}
                  onClick={() => setSelectedTemplate(tmpl)}
                  className={`p-3 rounded-lg border transition-all cursor-pointer ${
                    selectedTemplate.id === tmpl.id ? 'bg-blue-50 border-blue-600 ring-1 ring-blue-600' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-xs text-slate-900">{tmpl.templateKey}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                      {tmpl.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 font-medium">{tmpl.subject}</p>
                </div>
              ))}
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 text-xs">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="font-bold text-slate-900">{selectedTemplate.templateKey}</span>
                  <span className="text-slate-500 font-mono text-[11px]">{selectedTemplate.locale}</span>
                </div>

                <div>
                  <span className="text-slate-500 block text-[11px] font-bold">Subject Line:</span>
                  <span className="font-semibold text-slate-900 block mt-0.5 p-2 bg-white rounded border border-slate-200">
                    {selectedTemplate.subject}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 block text-[11px] font-bold">Preheader:</span>
                  <span className="text-slate-800 block mt-0.5 p-2 bg-white rounded border border-slate-200">
                    {selectedTemplate.preheader}
                  </span>
                </div>

                <div>
                  <span className="text-slate-500 block text-[11px] font-bold">Allowlisted Template Variables:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {selectedTemplate.variablesList.map(v => (
                      <span key={v} className="px-2 py-0.5 rounded bg-blue-100 text-blue-900 font-mono text-[11px] font-bold">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded text-rose-900 text-[11px]">
                  <strong>Strict Rule:</strong> Variables like <code>user_exact_salary</code> or <code>user_debt_balance</code> are strictly PROHIBITED from template injection.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'suppression' && (
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-rose-700" />
              <h3 className="text-sm font-bold text-slate-900">Centralized Suppression List & Unsubscribe Guard</h3>
            </div>
            <span className="px-2.5 py-1 rounded bg-rose-100 text-rose-900 font-bold text-xs">
              312 Suppressed Addresses
            </span>
          </div>

          <p className="text-xs text-slate-600 leading-relaxed">
            The Central Suppression List automatically intercepts unsubscribed emails, hard bounces, and spam complaints across all providers. Even if YamooPay migrates to a different email vendor, suppressed users are guaranteed never to receive promotional campaigns again.
          </p>

          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-700 space-y-1">
            <div className="flex justify-between py-1 border-b border-slate-200 font-bold text-slate-900">
              <span>Suppression Cause</span>
              <span>Count</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span>User Unsubscribe (Preference Center)</span>
              <span>240 addresses</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span>Hard Bounce (Invalid Mailbox)</span>
              <span>68 addresses</span>
            </div>
            <div className="flex justify-between py-1">
              <span>Spam Complaint</span>
              <span>4 addresses</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
